import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';

import Container from '../container/container';
import PostPreview from '../blog/post-preview';
import Pagination from '../pagination/pagination';
import LayoutContext from '../context/layout-context';
import type { Lucid } from '../../types/types';

export interface PostListProps {
  /** Posts to be displayed on this page */
  posts: Lucid.Directus.Generated.Blog_Post[];
  /** The root url alias for SEO crawling. */
  pathRoot: string;
  /** The current page being displayed in the pagination */
  currentPage: number;
  /** The total number of available pages */
  totalPageCount: number;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The new page handler callback. */
  handleNewPage?: (value: number) => void;
  /** The current search query */
  searchQuery?: string;
}

const PostList: FC<PropsWithChildren<PostListProps>> = ({
  posts,
  pathRoot,
  currentPage,
  totalPageCount,
  className,
  handleNewPage,
  searchQuery,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_blog_post_list'],
    shortCode,
  });

  return (
    <Container
      css={theme => ({
        label: 'blogPostList',
        display: 'flex',
        flexDirection: 'column',
        placeItems: 'center',
        paddingTop: theme.space.s120,
        paddingBottom: theme.space.s120,
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Pagination<Lucid.Directus.Generated.Blog_Post>
        aria-label={withSmartling('mktg_blog_post_list')}
        css={theme => ({
          width: '100%',
          fontSize: theme.fontSizes.fs20,
          svg: { height: '20px' },
          marginBottom: theme.space.s80,
        })}
        items={posts}
        itemsPerPage={12}
        onClick={handleNewPage}
        pageLink={page =>
          `${pathRoot}${page > 1 ? `/${page}` : ''}${`?${new URLSearchParams({ s: searchQuery ?? '' }).toString()}`}`
        }
        startPage={currentPage}
        totalPages={totalPageCount}
      >
        {({ visibleItems }) =>
          !!visibleItems.length && (
            <ul
              css={theme => ({
                display: 'grid',
                gridTemplateColumns: '1fr',
                listStyle: 'none',
                margin: '0',
                gap: theme.space.s40,
                [theme.breakpoints.bp768.min]: {
                  gridTemplateColumns: 'repeat(2, 1fr)',
                },
                [theme.breakpoints.bp1024.min]: {
                  gridTemplateColumns: 'repeat(4, 1fr)',
                },
              })}
            >
              {visibleItems.map((post, index) => (
                <PostPreview key={`blog-post-id-${post.id}-${index}`} {...post} />
              ))}
            </ul>
          )
        }
      </Pagination>
    </Container>
  );
};

export default PostList;
